import { useState, useCallback } from "react";

import { QuizAnswerOption } from "../../../gql/graphql";

export enum QuizGameState {
  INTRO = "intro",
  PLAYING = "playing",
  DEMOGRAPHICS = "demographics",
  SUMMARY = "summary",
}

export const useQuizState = (totalQuestions: number) => {
  const [gameState, setGameState] = useState<QuizGameState>(QuizGameState.PLAYING);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<QuizAnswerOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQuestion = currentQuestionIndex;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const totalPoints = selectedAnswers.reduce((sum, answer) => sum + (answer?.points || 0), 0);

  const startGame = useCallback(() => {
    setGameState(QuizGameState.PLAYING);
  }, []);

  const selectAnswer = useCallback(
    (selectedOption: QuizAnswerOption) => {
      const newAnswers = [...selectedAnswers, selectedOption];
      setSelectedAnswers(newAnswers);

      if (currentQuestionIndex === totalQuestions - 1) {
        setGameState(QuizGameState.DEMOGRAPHICS);
      } else {
        setCurrentQuestionIndex((prev) => prev + 1);
      }

      return newAnswers;
    },
    [selectedAnswers, currentQuestionIndex, totalQuestions],
  );

  const completeDemographics = useCallback(() => {
    setGameState(QuizGameState.SUMMARY);
  }, []);

  const resetQuiz = useCallback(() => {
    setGameState(QuizGameState.INTRO);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setIsSubmitting(false);
  }, []);

  return {
    gameState,
    currentQuestionIndex,
    selectedAnswers,
    isSubmitting,
    setIsSubmitting,
    currentQuestion,
    isLastQuestion,
    totalPoints,
    startGame,
    selectAnswer,
    completeDemographics,
    resetQuiz,
  };
};
